import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Space,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { GlobalApi } from '@Apis';
import { useOrgStore } from '../../../../../models/useOrgModel';
import { DeleteApprovedUsersDto } from '../../../../../types/globalSetting';

import { PolicyTypeEnum } from '../../../../Policy/utils/constants';
import LoadingBtn from '../../../../Report/LoadingBtn';
import { TransferChildRef } from '../../../Common/TransferForAddTargets/exchange';
import { PolicySubTypeEnum } from '../../../../../types';
import { PolicyType } from '../../../../../types/app';
import PolicyTypeSelect from './PolicyType';
import { useExchangeOnlineTargetUserGroups } from '@Policy/hooks';
import { SingleTargetUserGroups } from '@Policy/Components/SingleTargetUserGroups';
import { PolicyProvider } from '@Policy/store';
import { getExchangeTemplateConfig } from '@Policy/TemplateConfig/cfg.exchange';
import { useGlobalSettingStore } from '@Models/useGlobalSettingModel';

const AddUserModal = (props: {
  withEditPermission: boolean;
  visible: boolean;
  loading: boolean;
  policyType: PolicyType;
  policySubType: PolicySubTypeEnum;
  setLoading: (loading: boolean) => void;
  onClose: () => void;
  onReload: () => void;
  orgId: string;
}) => {
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const childRef = useRef<TransferChildRef | null>(null);
  const { orgId, orgsMap } = useOrgStore();
  const { selectedOrgGlobalSetting } = useGlobalSettingStore();
  const templateData = getExchangeTemplateConfig(
    t,
    selectedOrgGlobalSetting,
    ''
  );
  const configData = {};
  const TargetUserGroupsExchangeOnline = () => {
    const props = useExchangeOnlineTargetUserGroups();
    return <SingleTargetUserGroups {...props} ref={childRef} />;
  };
  const onClose = () => {
    props.onClose();
  };
  const policyTypeRef = useRef<any>(null);
  const onSave = async () => {
    const policyType = policyTypeRef.current.getValue();
    const users: DeleteApprovedUsersDto[] = [];
    if (
      childRef.current === null ||
      childRef?.current.checkedKeys.length === 0
    ) {
      return;
    }
    childRef?.current.checkedKeys.forEach((key) => {
      users.push({
        name: key.title as string,
        companyGuid: orgId,
        policyType: Number(policyType),
        email: key.email,
      });
    });
    // Deduplicate users within the same request
    const seen = new Set<string>();
    const uniqueUsers = users.filter((u) => {
      const key = `${u.policyType}#${u.email}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
    try {
      const res = await GlobalApi.addApprovedUsers(
        PolicyTypeEnum.Exchange,
        uniqueUsers
      );
      if (res.data) {
        window.showNotification({
          titleMsg: t('globalSetting.DNSDetectionExceptionList.add.success'),
          severity: 'success',
        });
        props.onReload();
      }
    } catch (e) {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    }
  };

  return (
    <Modal
      ensureFocus={true}
      autoFocus={false}
      closeOnEsc={true}
      closeOnOutsideClick={false}
      isClosable={true}
      isOpen={props.visible}
      onClose={onClose}
      size="md"
    >
      <ModalOverlay opacity={0.9} />
      <ModalContent>
        <ModalHeader>
          {t('globalSetting.approvedUser.addUser.title')}
        </ModalHeader>
        <ModalBody>
          <Box>
            <Flex>
              <Text
                color={colorStyle.color.secondary}
                lineHeight="lg"
                maxWidth={1000}
              >
                {t('globalSetting.approvedUser.addUser.desc')}
              </Text>
            </Flex>
            <Flex alignItems="center" m="8px 0px 12px 0px">
              <Text fontWeight={600}>
                {t('globalSetting.approvedUser.column.policyType')}:
              </Text>
              <Space width="12px" />
              <PolicyTypeSelect ref={policyTypeRef} />
            </Flex>
            <Box m="12px 0px">
              <Text>
                {t('common.label.currentOrganization')}:{' '}
                {`${orgsMap.get(orgId) ? orgsMap.get(orgId) : ''}`}
              </Text>
            </Box>
            <PolicyProvider
              template={templateData}
              config={configData}
              orgId={orgId}
            >
              <TargetUserGroupsExchangeOnline />
            </PolicyProvider>
          </Box>
        </ModalBody>
        <ModalFooter>
          <LoadingBtn
            loading={false}
            onClick={async () => {
              await onSave();
              props.onClose();
            }}
            variant="primary"
          >
            {t('common.button.ok')}
          </LoadingBtn>
          <Space width="2x" />
          <Button minWidth="20x" onClick={props.onClose}>
            {t('common.button.cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
export default React.memo(AddUserModal);
